import { DEFAULT_LOCATION } from 'config/defaultLocation.config';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useAddFusionPlacesMutation,
  useGetFusionPlacesQuery,
} from 'services/user/places/places.api';
import { selectUserSelectedLocation } from 'store/selectors/User/userSelectors';
import { setUserSelectedPlaceId } from 'store/slices/User/userSlice';
import { selectUserPlace } from 'store/thunks/User/userLocationThunks';
import {
  addPlaceToList,
  getSelectedPlace,
  removePlaceFromList,
  renamePlaceInList,
  setHomePlaceInList,
  sortPlaces,
} from '../utils/places.utils';

const usePlaces = () => {
  const dispatch = useDispatch();
  const selectedUserLocation = useSelector(selectUserSelectedLocation);
  const {
    data: placesData,
    isLoading: loadingPlaces,
    isFetching: fetchingPlaces,
    isSuccess: hasLoadedPlaces,
    refetch: refetchPlaces,
  } = useGetFusionPlacesQuery();
  const [addFusionPlaces, { isLoading: addingPlaces }] = useAddFusionPlacesMutation();
  const savePlacesInOrderRef = useRef(Promise.resolve());
  const pendingLocationSelectionRef = useRef(null);
  const hasAttemptedDefaultPlaceRef = useRef(false);

  // Sorts the list of places and ensures that places is always an array
  const places = useMemo(
    () => sortPlaces(Array.isArray(placesData) ? placesData : []),
    [placesData],
  );

  // Gets the full place object for the currently selected place from the RTK Query cache
  const fullSelectedPlaceObject = useMemo(
    () => getSelectedPlace(places, selectedUserLocation.placeId, selectedUserLocation.source),
    [places, selectedUserLocation],
  );

  // Adds a sorted array of new places to the user's list of saved places on the backend
  const savePlaces = useCallback(
    async (newPlaces) => {
      const sortedNewPlaces = sortPlaces(newPlaces);
      await addFusionPlaces(sortedNewPlaces).unwrap();
      return sortedNewPlaces;
    },
    [addFusionPlaces],
  );

  // Serialize saves from this hook instance; later saves still run after a failed request.
  const savePlacesInOrder = useCallback(
    (newPlaces) => {
      savePlacesInOrderRef.current = savePlacesInOrderRef.current
        .catch(() => {}) // Ignores errors from previous calls to prevent blocking the queue
        .then(() => savePlaces(newPlaces));

      return savePlacesInOrderRef.current;
    },
    [savePlaces],
  );

  // Calls the function to add a new place to the list of saved places on the backend
  const addPlace = useCallback(
    async (place) => {
      if (!place?.place_id) return null;
      const newPlaces = addPlaceToList(places, place);
      await savePlacesInOrder(newPlaces);
      return newPlaces;
    },
    [places, savePlacesInOrder],
  );

  const renamePlace = useCallback(
    async (placeId, newName) => {
      if (!placeId || !newName) return null;

      const newPlaces = renamePlaceInList(places, placeId, newName);
      await savePlacesInOrder(newPlaces);

      return newPlaces;
    },
    [places, savePlacesInOrder],
  );

  const removePlace = useCallback(
    async (placeId) => {
      if (!placeId) return null;

      const newPlaces = removePlaceFromList(places, placeId);
      await savePlacesInOrder(newPlaces);

      return newPlaces;
    },
    [places, savePlacesInOrder],
  );

  const selectPlace = useCallback(
    async (place) => {
      if (!place) return null;

      pendingLocationSelectionRef.current?.abort?.(); // Cancels any previous pending location selection to prevent race conditions

      const pendingLocationPromise = dispatch(selectUserPlace(place));
      pendingLocationSelectionRef.current = pendingLocationPromise;

      try {
        return await pendingLocationPromise.unwrap();
      } finally {
        if (pendingLocationSelectionRef.current === pendingLocationPromise) {
          pendingLocationSelectionRef.current = null;
        }
      }
    },
    [dispatch],
  );

  const changeHomePlace = useCallback(
    async (place) => {
      if (!place?.place_id) return null;

      const newPlaces = setHomePlaceInList(places, place.place_id);
      const newHomePlace = newPlaces?.find((newPlace) => newPlace.place_id === place.place_id);

      if (!newHomePlace) return null;

      await savePlacesInOrder(newPlaces);
      await selectPlace(newHomePlace);

      return newHomePlace;
    },
    [places, savePlacesInOrder, selectPlace],
  );

  useEffect(() => {
    if (!hasLoadedPlaces || loadingPlaces || fetchingPlaces) return;

    (async () => {
      if (places.length === 0) {
        // One attempt per empty-list period prevents mutation/refetch failures from retrying forever.
        if (hasAttemptedDefaultPlaceRef.current) return;
        hasAttemptedDefaultPlaceRef.current = true;
        await addPlace(DEFAULT_LOCATION);
        return;
      }
      hasAttemptedDefaultPlaceRef.current = false;

      if (
        selectedUserLocation.status === 'checking' ||
        selectedUserLocation.status === 'error' ||
        !selectedUserLocation.placeId
      ) {
        return;
      }

      if (!Cookies.get('selectedPlaceId')) {
        const homePlace = places.find((place) => place.DefaultLocationUserHome) || places[0];
        dispatch(setUserSelectedPlaceId(homePlace.place_id));
      }

      const matchingPlace = places.find((place) => place.place_id === selectedUserLocation.placeId);

      if (!matchingPlace) return;

      const matchingState = matchingPlace.state || matchingPlace.state_long;
      const matchingLatitude = matchingPlace.latitude ?? matchingPlace.geometry?.location?.lat;
      const matchingLongitude = matchingPlace.longitude ?? matchingPlace.geometry?.location?.lng;

      const alreadySelected =
        selectedUserLocation.city === matchingPlace.city &&
        selectedUserLocation.state === matchingState &&
        Number(selectedUserLocation.latitude) === Number(matchingLatitude) &&
        Number(selectedUserLocation.longitude) === Number(matchingLongitude);

      if (alreadySelected) return;

      await selectPlace(matchingPlace);
    })().catch(() => {
      // Automatic synchronization must not leave unhandled rejections. Explicit actions still reject
      // to their callers, and failed default-place initialization waits for a user action to retry.
    });
  }, [
    hasLoadedPlaces,
    loadingPlaces,
    fetchingPlaces,
    places,
    selectedUserLocation,
    selectPlace,
    addPlace,
    dispatch,
  ]);

  return {
    places,
    loadingPlaces,
    fetchingPlaces,
    addingPlaces,
    selectingPlace: selectedUserLocation.status === 'checking',
    refetchPlaces,
    fullSelectedPlaceObject,
    addPlace,
    removePlace,
    renamePlace,
    selectPlace,
    changeHomePlace,
  };
};

export default usePlaces;
